<template>
<div>
    <div class="form-row">
        <div class="col-8">
            <form-input label="Plan Name" v-model="formData.plan_name" :errors="errors.plan_name" type="text" :maxlength="128" />
        </div>

        <div class="col-4">
            <form-input label="Plan Type" v-model="formData.plan_type" :errors="errors.plan_type" type="select" :options="planTypeOptions" />
        </div>
    </div>

    <div class="form-row">
        <div class="col-12">
            <form-input
                class="insurance-plan-is-pretax-input"
                label="Is this plan pre-tax?"
                v-model="formData.is_pretax"
                :errors="errors.is_pretax"
                type="radio"
                :options="isPretaxOptions"
                helper-text="Pre-tax premiums come out of pay under a Section 125 cafeteria plan. This picks the deduction the plan runs under."
            />
        </div>
    </div>

    <div class="form-row" v-if="showPlanNumber">
        <div class="col-8">
            <form-input label="Plan Number" v-model="formData.plan_number" :errors="errors.plan_number" type="text" :maxlength="48" />
        </div>
    </div>

    <div class="form-row">
        <div class="col-6">
            <form-input label="Renewal Date" v-model="formData.starts_on" :errors="errors.starts_on" type="date" />
        </div>

        <div class="col-6">
            <form-input label="Termination Settings" v-model="formData.termination_mode" :errors="errors.termination_mode" type="select" :options="terminationSettingsOptions" />
        </div>
    </div>

    <div class="form-row">
        <div class="col-12">
            <form-input label="This plan includes an HSA" v-model="formData.has_hsa" :errors="errors.has_hsa" type="checkbox" />
        </div>
    </div>

    <h5 class="mt-4 mb-2">Billing</h5>

    <div class="form-row">
        <div class="col-12">
            <form-input class="insurance-plan-billing-type-input" label="" v-model="formData.billing_type" :errors="errors.billing_type" type="radio" :options="billingTypeOptions" />
        </div>
    </div>

    <div class="form-row" v-if="formData.billing_type == 'age-band'">
        <div class="col-6">
            <form-input label="Age Band Determination" v-model="formData.age_determination_method" :errors="errors.age_determination_method" type="select" :options="ageDeterminationMethodOptions" />
        </div>

        <div class="col-6" v-if="formData.age_determination_method == 'explicit'">
            <form-input label="Age Band Determination Date" v-model="formData.explicit_age_band_date" :errors="errors.explicit_age_band_date" type="date" />
        </div>
    </div>

    <div class="form-row mb-4">
        <div class="col-6">
            <form-input label="Employee Cost Covered by Employer" v-model="formData.employee_cost_covered" :errors="errors.employee_cost_covered" type="integer" placeholder="e.g. 65%" :maxlength="3"/>
        </div>

        <div class="col-6" v-if="formData.billing_type != 'age-band'">
            <form-input label="Dependent Cost Covered by Employer" v-model="formData.dependent_cost_covered" :errors="errors.dependent_cost_covered" type="integer" placeholder="e.g. 65%" :maxlength="3"/>
        </div>
    </div>

    <form-errors :errors="formErrors" />
</div>
</template>

<script>
import moment from 'moment'
import FormMixin from '@/mixins/Form'

const tomorrow = moment().add(1, 'days')

export default {
    props: ['initialValue', 'showPlanNumber', ],
    mixins: [FormMixin, ],
    computed: {
        // NB: strings, not booleans. A radio always hands back its value as a string,
        // and the API reads 'true'/'false' as a boolean.
        isPretaxOptions() {
            return [
                {text: 'Yes', value: 'true'},
                {text: 'No', value: 'false'},
            ]
        },
        planTypeOptions() {
            return [
                {text: 'Health', value: 'health'},
                {text: 'Dental', value: 'dental'},
                {text: 'Vision', value: 'vision'},
            ]
        },
        terminationSettingsOptions() {
            return [
                {text: 'Terminate at end of month', value: 'end-of-month'},
                {text: 'Terminate immediately', value: 'immediate'},
            ]
        },
        billingTypeOptions() {
            return [
                {text: 'Flat Amount', value: 'flat'},
                {text: 'Age Band', value: 'age-band'},
            ]
        },
        ageDeterminationMethodOptions() {
            return [
                {text: 'Beginning of the year', value: 'year-start'},
                {text: 'Plan start date', value: 'plan-start'},
                {text: 'Explicit date', value: 'explicit'},
            ]
        },
    },
    data() {
        return {
            formData: this.makeFormData(),
            formRules: {
                plan_name: {presence: {allowEmpty: false}, },
                plan_type: {presence: {allowEmpty: false}, },
                is_pretax: {presence: {allowEmpty: false}, },
                starts_on: {presence: {allowEmpty: false}, date: {earliest: tomorrow.format('YYYY-MM-DD'), tooEarly: "cannot be earlier than tomorrow."}},
                termination_mode: {presence: {allowEmpty: false}, },
                billing_type: {presence: {allowEmpty: false}, },

                employee_cost_covered: {presence: {allowEmpty: false}, numericality: {onlyInteger: true, greaterThanOrEqualTo: 0, lessThanOrEqualTo: 100}},
                dependent_cost_covered: {presence: {allowEmpty: false}, numericality: {onlyInteger: true, greaterThanOrEqualTo: 0, lessThanOrEqualTo: 100}},
            },
            formLabels: {
                'starts_on': 'Renewal Date',
                'is_pretax': 'Pre-tax',
            },
        }
    },
    methods: {
        makeFormData() {
            const starts_on = this.initialValue.starts_on ? moment(this.initialValue.starts_on, ['YYYY-MM-DD', 'MM/DD/YYYY']) : ''

            return {
                plan_name: this.initialValue.plan_name ? this.initialValue.plan_name : '',
                plan_number: this.initialValue.plan_number ? this.initialValue.plan_number : '',
                plan_type: this.initialValue.plan_type ? this.initialValue.plan_type : '',
                is_pretax: this.initialValue.is_pretax === undefined || this.initialValue.is_pretax === '' ? '' : '' + this.initialValue.is_pretax,
                starts_on: starts_on,
                termination_mode: this.initialValue.termination_mode ? this.initialValue.termination_mode : '',
                has_hsa: this.initialValue.has_hsa ? true : false,
                billing_type: this.initialValue.billing_type ? this.initialValue.billing_type : '',

                employee_cost_covered: this.initialValue.employee_cost_covered ? this.initialValue.employee_cost_covered : '',
                dependent_cost_covered: this.initialValue.dependent_cost_covered ? this.initialValue.dependent_cost_covered : '',
                age_determination_method: this.initialValue.age_determination_method ? this.initialValue.age_determination_method : '',
                explicit_age_band_date: this.initialValue.explicit_age_band_date ? this.initialValue.explicit_age_band_date : '',
            }
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRules))
            if (this.formData.billing_type == 'age-band') {
                rules.age_determination_method = {presence: {allowEmpty: false}}
                rules.dependent_cost_covered = {presence: {allowEmpty: true}}

                if (this.formData.age_determination_method == 'explicit') {
                    rules.explicit_age_band_date = {presence: {allowEmpty: false}, date: {earliest: tomorrow.format('YYYY-MM-DD'), tooEarly: "cannot be earlier than tomorrow."}}
                }
            }

            if (this.showPlanNumber) {
                rules.plan_number = {presence: {allowEmpty: false}}
            }

            return rules
        },
        setErrors(errors) {
            Object.assign(this.errors, errors)
        },
        getFormData() {
            const data = JSON.parse(JSON.stringify(this.formData))
            if (data.billing_type == 'age-band') {
                data.dependent_cost_covered = ''
            }
            else {
                data.age_determination_method = ''
            }

            if (data.age_determination_method != 'explicit') {
                data.explicit_age_band_date = ''
            }

            return data
        },
    },
}

</script>

<style>
.insurance-plan-billing-type-input>div,
.insurance-plan-is-pretax-input>div {
    margin-left: -1rem;
}
</style>
